import React, { useState } from 'react';
import { Modal, Select, Checkbox, Button, Tag } from 'antd';
import { DeactivateConfirmGlobalStyle } from './styledComponents';
import { GROUP_PREFIX, renderBillerAssignmentOptions } from './billerAssignmentOptions';

const authorTypeLabel = userType => (userType === 'doctor' ? 'Doctor' : 'Admin');

// Splits the merged picker values (biller ids and `group_<id>`) back into the
// billerConfig shape the API expects, same convention as the doctor form.
const toReassignTo = values => ({
  billers: values.filter(v => !String(v).startsWith(GROUP_PREFIX)),
  billerGroups: values
    .filter(v => String(v).startsWith(GROUP_PREFIX))
    .map(v => String(v).replace(GROUP_PREFIX, ''))
});

const DeactivateBillerGroupModal = ({
  visible,
  groupId,
  authors,
  billers,
  billerGroups,
  submitting,
  onCancel,
  onConfirm
}) => {
  const [targets, setTargets] = useState([]);
  const [updateExistingJobs, setUpdateExistingJobs] = useState(false);
  const hasAuthors = authors.length > 0;

  const onTargetsChange = values => {
    setTargets(values);
    if (values.length === 0) setUpdateExistingJobs(false);
  };

  const handleConfirm = () => {
    if (targets.length === 0) {
      onConfirm({ active: false });
      return;
    }
    onConfirm({
      active: false,
      reassignTo: toReassignTo(targets),
      updateExistingJobs
    });
  };

  return (
    <>
      <DeactivateConfirmGlobalStyle />
      <Modal
        title='Deactivate Biller Group'
        visible={visible}
        className='biller-deactivate-confirm'
        style={{ top: 150 }}
        onCancel={submitting ? undefined : onCancel}
        maskClosable={!submitting}
        closable={!submitting}
        destroyOnClose
        footer={[
          <Button
            key='cancel'
            onClick={onCancel}
            disabled={submitting}
            style={{ borderRadius: 8 }}
          >
            Cancel
          </Button>,
          <Button
            key='deactivate'
            type='primary'
            danger
            loading={submitting}
            onClick={handleConfirm}
            style={{ borderRadius: 8 }}
          >
            Deactivate
          </Button>
        ]}
      >
        {hasAuthors ? (
          <div>
            <p>This group is currently assigned to the following doctors and admins:</p>
            <ul style={{ paddingLeft: 20, margin: '8px 0', maxHeight: 200, overflowY: 'auto' }}>
              {authors.map(a => (
                <li key={a.id} style={{ marginBottom: 4 }}>
                  <span>{a.fullName}</span>{' '}
                  <Tag style={{ marginLeft: 4 }}>{authorTypeLabel(a.userType)}</Tag>
                </li>
              ))}
            </ul>
            <p style={{ color: '#8c8c8c' }}>
              These doctors and admins will keep this group, and new jobs will still go
              to its billers. No one else can be assigned to it. Pick a biller or group
              below to move them now.
            </p>
            <p style={{ marginBottom: 4 }}>Reassign to</p>
            <Select
              className='reassign-target-select'
              mode='multiple'
              size='large'
              placeholder='Select billers or groups (optional)'
              optionFilterProp='children'
              showSearch
              allowClear
              virtual={false}
              value={targets}
              onChange={onTargetsChange}
              disabled={submitting}
              style={{ width: '100%' }}
            >
              {/* The group being deactivated is left out; other inactive ones are listed but disabled */}
              {renderBillerAssignmentOptions(billers, billerGroups, groupId)}
            </Select>
            <Checkbox
              style={{ marginTop: 12 }}
              checked={updateExistingJobs}
              disabled={targets.length === 0 || submitting}
              onChange={e => setUpdateExistingJobs(e.target.checked)}
            >
              Also reassign unbilled jobs
            </Checkbox>
          </div>
        ) : (
          <p>Are you sure you want to deactivate this group?</p>
        )}
      </Modal>
    </>
  );
};

export default DeactivateBillerGroupModal;
